<!-- Copyright 2020 Adobe. All rights reserved.
This file is licensed to you under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License. You may obtain a copy
of the License at http://www.apache.org/licenses/LICENSE-2.0
Unless required by applicable law or agreed to in writing, software distributed under
the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
OF ANY KIND, either express or implied. See the License for the specific language
governing permissions and limitations under the License. -->

import {BlogPostLayout, Hero} from '@react-spectrum/docs';
export default BlogPostLayout;

---
description: This release we give you plenty of bug fixes, and a new component – NumberField! This component is stacked with features and helpful accessibility and internationalization support. NumberField offers internationalized number formatting and parsing, support for the Latin, Arabic, and Han decimal numbering systems, validation, allows incrementing/decrementing via stepper buttons, arrow keys, and scrolling, and much more.
date: 2021-05-05
---

# May 5, 2021 Release

This release we give you plenty of bug fixes, and a new component – **NumberField**! This component is stacked with features and helpful accessibility and internationalization support. NumberField offers internationalized number formatting and parsing, support for the Latin, Arabic, and Han decimal numbering systems, validation, allows incrementing/decrementing via stepper buttons, arrow keys, and scrolling, and much more. See a full list of functionality in our [React Spectrum](https://react-spectrum.adobe.com/react-spectrum/NumberField.html), [React Aria](https://react-spectrum.adobe.com/react-aria/useNumberField.html) and [React Stately](https://react-spectrum.adobe.com/react-stately/useNumberFieldState.html) docs. Also you can learn more about the complexities of NumberField by reading our latest [blog post](https://react-spectrum.adobe.com/blog/how-we-internationalized-our-numberfield.html).

Thank you to all our contributors for their help with this release.

## New component

- **NumberField**
    - `@react-spectrum/numberfield` - [Docs](https://react-spectrum.adobe.com/react-spectrum/NumberField.html)
    - `@react-aria/numberfield` - [Docs](https://react-spectrum.adobe.com/react-aria/useNumberField.html)
    - `@react-stately/numberfield` - [Docs](https://react-spectrum.adobe.com/react-stately/useNumberFieldState.html)

## New features

- Expose `isPressed` in `useLink` - [@hyanmandian](https://github.com/hyanmandian) - [PR](https://github.com/adobe/react-spectrum/pull/1767)
- Allow specifying a selector for `OverlayContainer` - [@TLadd](https://github.com/TLadd) - [PR](https://github.com/adobe/react-spectrum/pull/1013)
- Update scrolling behavior of `ColorField` - [@snowystinger](https://github.com/snowystinger) - [PR](https://github.com/adobe/react-spectrum/pull/1654)
- Update `Table` animations for initial load - [@devongovett](https://github.com/devongovett) - [PR](https://github.com/adobe/react-spectrum/pull/1789)
- Add custom width support for `Meter` and `ProgressBar` - [@ktabors](https://github.com/ktabors) - [PR](https://github.com/adobe/react-spectrum/pull/1560)
- Show all items when manually opening `ComboBox` - [@LFDanLu](https://github.com/LFDanLu) - [PR](https://github.com/adobe/react-spectrum/pull/1749)
- Add blur and focus events to `NumberField` - [@ktabors](https://github.com/ktabors) - [PR](https://github.com/adobe/react-spectrum/pull/1738)

## Fixed

- Remove unused assignments - [@tomsontom](https://github.com/tomsontom) - [PR](https://github.com/adobe/react-spectrum/pull/1745)
- Remove conditional spacing in `Label` - [@tomsontom](https://github.com/tomsontom) - [PR](https://github.com/adobe/react-spectrum/pull/1733)
- Fix useTreeState to rebuild when node is expanded - [@lukasbuenger](https://github.com/lukasbuenger) - [PR](https://github.com/adobe/react-spectrum/pull/1711)
- Add warning to usage of useDrag1D - [@snowystinger](https://github.com/snowystinger) - [PR](https://github.com/adobe/react-spectrum/pull/1757)
- Only scroll step when `NumberField` is focused - [@snowystinger](https://github.com/snowystinger) - [PR](https://github.com/adobe/react-spectrum/pull/1615)
- Remove unused props from `LinkProps` - [@chrishoage](https://github.com/chrishoage) - [PR](https://github.com/adobe/react-spectrum/pull/1763)
- Update peer dependencies in aria and stately - [@snowystinger](https://github.com/snowystinger) - [PR](https://github.com/adobe/react-spectrum/pull/1780)
- Update design for `Tabs` - [@devongovett](https://github.com/devongovett) - [PR](https://github.com/adobe/react-spectrum/pull/1785)
- Improve focus handling on dropped items in `dnd` - [@devongovett](https://github.com/devongovett) - [PR](https://github.com/adobe/react-spectrum/pull/1721)
- Remove erroneous type of button from `useButton` - [@snowystinger](https://github.com/snowystinger) - [PR](https://github.com/adobe/react-spectrum/pull/1772)
- Filter out hidden elements in `FocusScope` - [@majornista](https://github.com/majornista) - [PR](https://github.com/adobe/react-spectrum/pull/1493)
- Fix moving children within the same parent node in `useTreeData` - [@snowystinger](https://github.com/snowystinger) - [PR](https://github.com/adobe/react-spectrum/pull/1465)
- Fix overlay rendering on mount - [@intergalacticspacehighway](https://github.com/intergalacticspacehighway) - [PR](https://github.com/adobe/react-spectrum/pull/1695)
- Fix diagonally scrolling in `NumberField` - [@snowystinger](https://github.com/snowystinger) - [PR](https://github.com/adobe/react-spectrum/pull/1802)
- Remove selection styles in `Table` when selection is off - [@LFDanLu](https://github.com/LFDanLu) - [PR](https://github.com/adobe/react-spectrum/pull/1787)
- Update `ComboBox` value when selection loads/updates - [@LFDanLu](https://github.com/LFDanLu) - [PR](https://github.com/adobe/react-spectrum/pull/1683)
- Fix alignment of `Tab` selected style - [@snowystinger](https://github.com/snowystinger) - [PR](https://github.com/adobe/react-spectrum/pull/1821)
- Disable text selection in `Tabs` - [@devongovett](https://github.com/devongovett) - [PR](https://github.com/adobe/react-spectrum/pull/1822)
- Only scroll step when `ColorField` is focused - [@snowystinger](https://github.com/snowystinger) - [PR](https://github.com/adobe/react-spectrum/pull/1823)
- Fix transition icons on hover in `Tabs` - [@devongovett](https://github.com/devongovett) - [PR](https://github.com/adobe/react-spectrum/pull/1827)
- Remove unused dependency in `Button` - [@Andarist](https://github.com/Andarist) - [PR](https://github.com/adobe/react-spectrum/pull/1828)
- Fix `useAsyncList` loading more skip logic - [@LFDanLu](https://github.com/LFDanLu) - [PR](http://github.com/adobe/react-spectrum/pull/1829/)
- Add key to `Tabs` to prevent children sharing - [@ktabors](https://github.com/ktabors) - [PR](https://github.com/adobe/react-spectrum/pull/1836)

## Under construction

Pre-release versions of the following components have been released. Please feel free to try them out, and report any issues you encounter.

- Tabs rc
    - [React Spectrum](https://react-spectrum.adobe.com/react-spectrum/Tabs.html) – Please note there was an API update in this version. Read our [upgrade guide](https://gist.github.com/devongovett/86f49cf669b4178d03fcd73c50a2cc4a) for more information.
- ComboBox beta
    - [React Spectrum](https://react-spectrum.adobe.com/react-spectrum/ColorSlider.html)
    - [React Aria](https://react-spectrum.adobe.com/react-aria/useColorSlider.html)
- ColorSlider beta
    - [React Spectrum](https://react-spectrum.adobe.com/react-spectrum/ColorSlider.html)
    - [React Aria](https://react-spectrum.adobe.com/react-aria/useColorSlider.html)
- ColorWheel beta
    - [React Spectrum](https://react-spectrum.adobe.com/react-spectrum/ColorWheel.html)
    - [React Aria](https://react-spectrum.adobe.com/react-aria/useColorWheel.html)
- ColorField beta
    - [React Spectrum](https://react-spectrum.adobe.com/react-spectrum/ColorField.html)
    - [React Aria](https://react-spectrum.adobe.com/react-aria/useColorField.html)

## Released packages

```
- @adobe/react-spectrum@3.10.0
- @internationalized/message@3.0.0
- @internationalized/number@3.0.0
- @react-aria/accordion@3.0.0-alpha.1
- @react-aria/actiongroup@3.1.3
- @react-aria/aria-modal-polyfill@3.3.1
- @react-aria/breadcrumbs@3.1.4
- @react-aria/button@3.3.2
- @react-aria/checkbox@3.2.2
- @react-aria/color@3.0.0-beta.2
- @react-aria/combobox@3.0.0-beta.1
- @react-aria/dialog@3.1.3
- @react-aria/dnd@3.0.0-alpha.1
- @react-aria/focus@3.3.0
- @react-aria/grid@3.0.0-alpha.2
- @react-aria/i18n@3.3.1
- @react-aria/interactions@3.4.0
- @react-aria/label@3.1.2
- @react-aria/link@3.1.3
- @react-aria/listbox@3.2.5
- @react-aria/live-announcer@3.0.0
- @react-aria/menu@3.2.1
- @react-aria/meter@3.1.2
- @react-aria/numberfield@3.0.0
- @react-aria/overlays@3.6.3
- @react-aria/progress@3.1.2
- @react-aria/radio@3.1.4
- @react-aria/searchfield@3.1.3
- @react-aria/select@3.3.2
- @react-aria/selection@3.4.1
- @react-aria/separator@3.1.2
- @react-aria/slider@3.0.2
- @react-aria/spinbutton@3.0.0
- @react-aria/ssr@3.0.2
- @react-aria/switch@3.1.2
- @react-aria/table@3.0.0-alpha.11
- @react-aria/tabs@3.0.0-rc.0
- @react-aria/textfield@3.3.0
- @react-aria/toggle@3.1.3
- @react-aria/tooltip@3.1.2
- @react-aria/utils@3.8.0
- @react-aria/virtualizer@3.3.3
- @react-aria/visually-hidden@3.2.2
- @react-spectrum/accordion@3.0.0-alpha.1
- @react-spectrum/actiongroup@3.1.3
- @react-spectrum/breadcrumbs@3.2.2
- @react-spectrum/button@3.4.1
- @react-spectrum/buttongroup@3.2.1
- @react-spectrum/checkbox@3.2.3
- @react-spectrum/color@3.0.0-beta.2
- @react-spectrum/combobox@3.0.0-beta.1
- @react-spectrum/dialog@3.3.2
- @react-spectrum/divider@3.1.2
- @react-spectrum/form@3.2.2
- @react-spectrum/icon@3.3.1
- @react-spectrum/illustratedmessage@3.1.2
- @react-spectrum/image@3.1.2
- @react-spectrum/label@3.3.3
- @react-spectrum/layout@3.1.4
- @react-spectrum/link@3.1.2
- @react-spectrum/listbox@3.4.2
- @react-spectrum/menu@3.2.3
- @react-spectrum/meter@3.1.2
- @react-spectrum/numberfield@3.0.0
- @react-spectrum/overlays@3.4.1
- @react-spectrum/picker@3.2.4
- @react-spectrum/progress@3.1.2
- @react-spectrum/provider@3.1.4
- @react-spectrum/radio@3.1.3
- @react-spectrum/searchfield@3.1.4
- @react-spectrum/slider@3.0.3
- @react-spectrum/statuslight@3.2.1
- @react-spectrum/switch@3.1.2
- @react-spectrum/table@3.0.0-alpha.12
- @react-spectrum/tabs@3.0.0-rc.0
- @react-spectrum/text@3.1.2
- @react-spectrum/textfield@3.1.6
- @react-spectrum/theme-dark@3.2.1
- @react-spectrum/theme-default@3.2.1
- @react-spectrum/theme-light@3.1.1
- @react-spectrum/tooltip@3.1.3
- @react-spectrum/utils@3.5.2
- @react-spectrum/view@3.1.2
- @react-spectrum/well@3.1.2
- @react-stately/checkbox@3.0.2
- @react-stately/collections@3.3.2
- @react-stately/color@3.0.0-beta.2
- @react-stately/combobox@3.0.0-beta.1
- @react-stately/data@3.4.0
- @react-stately/dnd@3.0.0-alpha.1
- @react-stately/grid@3.0.0-alpha.2
- @react-stately/layout@3.2.2
- @react-stately/list@3.2.3
- @react-stately/menu@3.2.2
- @react-stately/numberfield@3.0.0
- @react-stately/overlays@3.1.2
- @react-stately/radio@3.3.1
- @react-stately/searchfield@3.1.2
- @react-stately/select@3.1.2
- @react-stately/selection@3.5.0
- @react-stately/slider@3.0.2
- @react-stately/table@3.0.0-alpha.10
- @react-stately/tabs@3.0.0-rc.0
- @react-stately/toggle@3.2.2
- @react-stately/tooltip@3.0.4
- @react-stately/tree@3.1.4
- @react-stately/utils@3.2.1
- @react-stately/virtualizer@3.1.4
- @react-types/combobox@3.0.0-beta.1
- @react-types/link@3.1.2
- @react-types/numberfield@3.0.0
- @react-types/shared@3.6.0
- @react-types/tabs@3.0.0-rc.0
- react-aria@3.6.0
- react-stately@3.5.0
```
